import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTheme } from '@paige/ui';
import { useAuthStore, usePrincipal } from '../auth/AuthBoundary';
import { openHelp } from '../screens/help/help-view';

/**
 * The way out, shared by the dashboard's profile menu and the workbench's workspace menu: end the
 * session, then the sign-in page. One function, so the two menus cannot drift.
 */
export function useSignOut(): () => Promise<void> {
  const store = useAuthStore();
  const navigate = useNavigate();
  return useCallback(async () => {
    await store.signOut();
    await navigate({ to: '/sign-in', search: {} });
  }, [store, navigate]);
}

/** The person the workbench's account door shows: `paigeWorkbench.person`. */
export interface WorkbenchPerson {
  name: string;
  email: string;
  company: string;
  signOut: () => void;
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  openSettings: () => void;
  openAdmin?: () => void;
  openHelp: () => void;
}

/**
 * The person as the workbench's account door shows them (bottom left, where CapturePoint has This
 * computer; Dustin, 2026-09-24): name, the company the job is in, light or dark, the company's
 * settings and Sign out. One object per person, company and theme, so the workbench's bridge is not
 * rebuilt every render.
 */
export function useWorkbenchPerson(tenantId: string): WorkbenchPerson {
  const principal = usePrincipal();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { resolved, setPreference } = useTheme();
  const company = principal.tenants.find((t) => t.id === tenantId)?.name ?? tenantId;
  return useMemo(
    () => ({
      name: principal.name,
      email: principal.email,
      company,
      signOut: () => void signOut(),
      theme: resolved,
      setTheme: (theme: 'light' | 'dark') => setPreference(theme),
      openSettings: () =>
        void navigate({ to: '/t/$tenant/settings', params: { tenant: tenantId } }),
      // Staff only (the API's staff claim, as AdminMark): the admin area from a job's own page.
      ...(principal.staff ? { openAdmin: () => void navigate({ to: '/admin', search: {} }) } : {}),
      // Help, the profile menu's own sheet, over the workbench (screens/help).
      openHelp: () =>
        openHelp(tenantId, 'help', document.querySelector<HTMLElement>('.account-door-button')),
    }),
    [
      principal.staff,
      principal.name,
      principal.email,
      company,
      signOut,
      resolved,
      setPreference,
      navigate,
      tenantId,
    ],
  );
}
